import React from "react";
import { cn } from "../lib/utils";
import type { SkeletonProps } from "../shared/types/component.types";

export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  variant = "rectangular",
  width,
  height,
}) => {
  const baseClasses = "bg-tiber-200 rounded";
  const variantClasses = {
    text: "h-4 rounded",
    circular: "rounded-full",
    rectangular: "rounded",
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === "number" ? `${width}px` : width;
  if (height)
    style.height = typeof height === "number" ? `${height}px` : height;

  return (
    <div
      className={cn(baseClasses, variantClasses[variant], className)}
      style={style}
    />
  );
};

// Pre-built skeleton components
export const SkeletonText: React.FC<{
  lines?: number;
  className?: string;
}> = ({ lines = 1, className }) => {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? "75%" : "100%"}
        />
      ))}
    </div>
  );
};

export const SkeletonCard: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <div
      className={cn(
        "bg-white rounded-lg p-6 border border-tiber-200 shadow-sm",
        className
      )}
    >
      <Skeleton variant="text" width="60%" height={24} className="mb-4" />
      <SkeletonText lines={3} />
      <div className="mt-4 flex gap-2">
        <Skeleton variant="rectangular" width={100} height={36} />
        <Skeleton variant="rectangular" width={100} height={36} />
      </div>
    </div>
  );
};

export const SkeletonTable: React.FC<{
  rows?: number;
  columns?: number;
  className?: string;
}> = ({ rows = 5, columns = 4, className }) => {
  return (
    <div className={cn("space-y-2", className)}>
      {/* Header */}
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} variant="text" width="100%" height={20} />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} variant="text" width="100%" height={16} />
          ))}
        </div>
      ))}
    </div>
  );
};

export const SkeletonAvatar: React.FC<{
  size?: number;
  className?: string;
}> = ({ size = 40, className }) => {
  return (
    <Skeleton
      variant="circular"
      width={size}
      height={size}
      className={className}
    />
  );
};

export const SkeletonStatsCard: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <div
      className={cn(
        "rounded-xl p-4 sm:p-6 shadow-lg animate-pulse",
        className
      )}
    >
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <Skeleton variant="rectangular" width={48} height={48} className="rounded-lg" />
        <Skeleton variant="rectangular" width={20} height={20} className="rounded" />
      </div>
      <Skeleton variant="text" width="60%" height={16} className="mb-2" />
      <Skeleton variant="text" width="40%" height={32} />
    </div>
  );
};

export const SkeletonButton: React.FC<{
  width?: string | number;
  height?: number;
  className?: string;
}> = ({ width = "100%", height = 40, className }) => {
  return (
    <Skeleton
      variant="rectangular"
      width={width}
      height={height}
      className={cn("rounded-lg", className)}
    />
  );
};

export const SkeletonBadge: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <Skeleton
      variant="rectangular"
      width={80}
      height={24}
      className={cn("rounded-full", className)}
    />
  );
};
